export const DEFAULT_WORKSPACE_TIMEZONE = 'Australia/Sydney';

/**
 * Resolves a calendar date ('YYYY-MM-DD') and seconds-from-midnight to an epoch timestamp (seconds)
 * in the specified IANA time zone (e.g., 'Australia/Sydney', 'America/New_York', 'UTC').
 * Correctly accounts for Daylight Saving Time transitions.
 */
export function resolveOffsetToInstant(
  serviceDate: string,
  secondsFromMidnight: number,
  timeZone: string = DEFAULT_WORKSPACE_TIMEZONE
): number {
  const [yearStr, monthStr, dayStr] = serviceDate.split('-');
  const year = Number.parseInt(yearStr, 10);
  const month = Number.parseInt(monthStr, 10);
  const day = Number.parseInt(dayStr, 10);

  const hours = Math.floor(secondsFromMidnight / 3600);
  const remainder = secondsFromMidnight % 3600;
  const minutes = Math.floor(remainder / 60);
  const seconds = remainder % 60;

  // Approximate UTC instant from naive components
  let targetUtc = Date.UTC(year, month - 1, day, hours, minutes, seconds);

  // Use Intl.DateTimeFormat to measure the actual offset in the given timezone at targetUtc
  const dtf = new Intl.DateTimeFormat('en-US', {
    timeZone,
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
    second: 'numeric',
    hour12: false,
  });

  const getParts = (epochMs: number) => {
    const parts = dtf.formatToParts(new Date(epochMs));
    const map: Record<string, number> = {};
    for (const part of parts) {
      if (part.type !== 'literal') {
        map[part.type] = Number.parseInt(part.value, 10);
      }
    }
    // hour might format as 24 in some engines
    const h = (map.hour ?? 0) === 24 ? 0 : (map.hour ?? 0);
    return Date.UTC(map.year, map.month - 1, map.day, h, map.minute ?? 0, map.second ?? 0);
  };

  // Adjust for timezone offset
  const localMs = getParts(targetUtc);
  const diff = targetUtc - localMs;
  targetUtc += diff;

  // Re-verify in case offset boundary crossed
  const reLocalMs = getParts(targetUtc);
  if (reLocalMs !== Date.UTC(year, month - 1, day, hours, minutes, seconds)) {
    const reDiff = Date.UTC(year, month - 1, day, hours, minutes, seconds) - reLocalMs;
    targetUtc += reDiff;
  }

  return Math.floor(targetUtc / 1000);
}

/**
 * Resolves an epoch timestamp (seconds) to a service date ('YYYY-MM-DD') and seconds-from-midnight
 * in the specified IANA time zone.
 */
export function resolveInstantToOffset(
  epochSeconds: number,
  timeZone: string = DEFAULT_WORKSPACE_TIMEZONE
): { serviceDate: string; secondsFromMidnight: number } {
  const dtf = new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  });

  const parts = dtf.formatToParts(new Date(epochSeconds * 1000));
  const map: Record<string, string> = {};
  for (const part of parts) {
    if (part.type !== 'literal') {
      map[part.type] = part.value;
    }
  }

  const serviceDate = `${map.year}-${map.month}-${map.day}`;
  const hours = Number.parseInt(map.hour, 10) === 24 ? 0 : Number.parseInt(map.hour, 10);
  const minutes = Number.parseInt(map.minute, 10);
  const seconds = Number.parseInt(map.second, 10);
  const secondsFromMidnight = hours * 3600 + minutes * 60 + seconds;

  return { serviceDate, secondsFromMidnight };
}

/**
 * Formats seconds-from-midnight (e.g. 28800) to 'HH:MM' (e.g. '08:00').
 */
export function formatOffsetTime(secondsFromMidnight: number): string {
  const hours = Math.floor(secondsFromMidnight / 3600);
  const minutes = Math.floor((secondsFromMidnight % 3600) / 60);
  return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
}

/**
 * Parses 'HH:MM' (e.g. '08:30') to seconds-from-midnight (30600).
 */
export function parseOffsetTime(timeStr: string): number {
  const [hStr, mStr] = timeStr.split(':');
  const hours = Number.parseInt(hStr, 10) || 0;
  const minutes = Number.parseInt(mStr, 10) || 0;
  return hours * 3600 + minutes * 60;
}

/**
 * Renders an instant in the workspace zone with formatted strings.
 */
export function formatInstantInZone(
  epochSeconds: number,
  format: 'time' | 'date' | 'datetime',
  timeZone: string = DEFAULT_WORKSPACE_TIMEZONE
): string {
  const date = new Date(epochSeconds * 1000);
  if (format === 'time') {
    return new Intl.DateTimeFormat('en-US', {
      timeZone,
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    }).format(date);
  }
  if (format === 'date') {
    return new Intl.DateTimeFormat('en-US', {
      timeZone,
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    }).format(date);
  }
  return new Intl.DateTimeFormat('en-US', {
    timeZone,
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  }).format(date);
}

/**
 * Calculates Haversine distance in meters between two lat/lng coordinates.
 */
export function haversineDistanceMeters(
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number
): number {
  const R = 6371000.0; // Earth radius in meters
  const phi1 = (lat1 * Math.PI) / 180.0;
  const phi2 = (lat2 * Math.PI) / 180.0;
  const deltaPhi = ((lat2 - lat1) * Math.PI) / 180.0;
  const deltaLambda = ((lon2 - lon1) * Math.PI) / 180.0;

  const a =
    Math.sin(deltaPhi / 2.0) ** 2 +
    Math.cos(phi1) * Math.cos(phi2) * Math.sin(deltaLambda / 2.0) ** 2;
  const c = 2.0 * Math.atan2(Math.sqrt(a), Math.sqrt(1.0 - a));
  return R * c;
}

/**
 * Estimates transit duration in seconds between two coordinates.
 * Assumes 40 km/h urban average speed (~11.11 m/s) with a 1.3 road curvature factor.
 */
export function estimateTransitSeconds(
  lat1?: number | null,
  lon1?: number | null,
  lat2?: number | null,
  lon2?: number | null,
  fallbackSeconds = 0
): number {
  if (lat1 == null || lon1 == null || lat2 == null || lon2 == null) {
    return fallbackSeconds;
  }
  const distM = haversineDistanceMeters(lat1, lon1, lat2, lon2) * 1.3;
  const speedMps = (40.0 * 1000.0) / 3600.0; // ~11.11 m/s
  return Math.max(0, Math.round(distM / speedMps));
}
